/**
 * QuickTapsTableV2 — Quick Taps list, ported from the prototype's
 * `.screen-quicktaps` (cms-redesign-mockup.html ~line 5840+). Shares
 * the `.sessions` table styling, columns, and row treatment with the
 * Sessions list — quick taps are short sessions, so the visual
 * language is the same.
 *
 * Server-paginated against the new dual-mode `GET /quick-tap-sessions`
 * endpoint. We always pass `page` + `per_page` so the response is the
 * paginated envelope (`QuickTapSessionListPaginatedResponse`):
 *
 *   { data, page, per_page, page_count, has_more, total_count }
 *
 * Quick taps differ from sessions: a single nested `category` OR
 * `subcategory` parent (not arrays), no series / challenge / audiobook
 * parents. `resolveParent` picks the more specific one.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchQuickTapsPage } from '../../../actions/quickTapsActions';
import {
  LibTable,
  ListFooter,
  TitleCell,
  ParentChip,
  TierPill,
  AuthorCell,
  DurationCell,
  StatusEdge,
  ChipFilter,
  SegmentedControl,
  IconButton,
} from '../../redesign';

/* ─── Field adapters ─────────────────────────────────────────────────── */

function resolveParent(qt) {
  if (qt.subcategory) {
    return { type: 'subcategory', name: qt.subcategory.subcategory_title };
  }
  if (qt.category) {
    return { type: 'category', name: qt.category.category_title };
  }
  return { type: 'none', name: null };
}

function coverInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function formatRelativeTime(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day === 0) {
    if (hr  >= 1) return `${hr}h ago`;
    if (min >= 1) return `${min}m ago`;
    return 'just now';
  }
  if (day < 7)   return `${day}d ago`;
  if (day < 30)  return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Column widths — same as Sessions for visual consistency. */
const COL_WIDTHS = {
  status:  22,
  title:   'auto',
  parent:  300,
  length:  70,
  tier:    95,
  author:  160,
  // Bumped from 80 → 100 — see SessionsTableV2 for the rationale.
  created: 100,
  actions: 60,
};

function QuickTapsColgroup() {
  return (
    <colgroup>
      <col style={{ width: COL_WIDTHS.status }} />
      <col />
      <col style={{ width: COL_WIDTHS.parent }} />
      <col style={{ width: COL_WIDTHS.length }} />
      <col style={{ width: COL_WIDTHS.tier }} />
      <col style={{ width: COL_WIDTHS.author }} />
      <col style={{ width: COL_WIDTHS.created }} />
      <col style={{ width: COL_WIDTHS.actions }} />
    </colgroup>
  );
}

function QuickTapsHead() {
  return (
    <LibTable.Head>
      <tr>
        <LibTable.Th style={{ paddingLeft: 28 }}>
          <span className="sr-only">Status</span>
        </LibTable.Th>
        <LibTable.Th>Quick Tap</LibTable.Th>
        <LibTable.Th>Parent</LibTable.Th>
        <LibTable.Th>Length</LibTable.Th>
        <LibTable.Th>Tier</LibTable.Th>
        <LibTable.Th>Author</LibTable.Th>
        <LibTable.Th>Created</LibTable.Th>
        <LibTable.Th> </LibTable.Th>
      </tr>
    </LibTable.Head>
  );
}

/* ─── Row ──────────────────────────────────────────────────────────── */
function QuickTapRow({ qt }) {
  const parent = resolveParent(qt);
  const badges = [];
  if (qt.is_featured) badges.push({ label: 'Featured', tone: 'featured' });
  if (qt.is_new)      badges.push({ label: 'New', tone: 'new' });

  const inactive = !qt.is_active;
  const author = qt.author;

  return (
    <LibTable.Row inactive={inactive}>
      <td
        className="relative border-b border-line group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ padding: 0 }}
      >
        <StatusEdge active={!inactive} />
      </td>
      <LibTable.Td>
        <TitleCell
          coverImage={qt.image}
          coverInitials={coverInitials(qt.title)}
          coverSeed={qt.title}
          title={qt.title}
          badges={badges}
        />
      </LibTable.Td>
      <LibTable.Td>
        <ParentChip type={parent.type} name={parent.name} />
      </LibTable.Td>
      <LibTable.Td>
        {qt.duration
          ? <DurationCell value={qt.duration} />
          : <DurationCell seconds={Number(qt.duration_in_sec) || null} />}
      </LibTable.Td>
      <LibTable.Td>
        <TierPill isFree={!qt.is_premium} />
      </LibTable.Td>
      <LibTable.Td>
        {author?.author_name ? (
          <AuthorCell name={author.author_name} imageUrl={author.author_image} />
        ) : (
          <span className="text-text-light" style={{ fontSize: 12 }}>—</span>
        )}
      </LibTable.Td>
      <LibTable.Td>
        <span className="font-mono text-text-muted" style={{ fontSize: 12.5 }}>
          {formatRelativeTime(qt.created_at)}
        </span>
      </LibTable.Td>
      <LibTable.Td>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link to={`/quick-taps/edit/${qt.id}`} aria-label="Edit quick tap">
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Filter chip → API param translation ──────────────────────────── */
function paramsForFilters({ activeFilter, featuredFilter, premiumFilter }) {
  const out = {};
  if (activeFilter   === 'active')   out.active = '1';
  if (activeFilter   === 'inactive') out.active = '0';
  if (featuredFilter === 'yes')      out.featured = '1';
  if (premiumFilter  === 'free')     out.free = '1';
  if (premiumFilter  === 'premium')  out.free = '0';
  return out;
}

/* ─── Main component ──────────────────────────────────────────────── */
export default function QuickTapsTableV2() {
  const dispatch = useDispatch();
  const paginated = useSelector((s) => s.quick_taps.paginated);
  const loading   = useSelector((s) => s.quick_taps.paginatedLoading);
  const error     = useSelector((s) => s.quick_taps.paginatedError);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [activeFilter,   setActiveFilter]   = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [premiumFilter,  setPremiumFilter]  = useState('all');

  // Reset to page 1 whenever a filter or per-page changes — landing on
  // page 17 of a freshly-filtered set is almost never what you want.
  useEffect(() => {
    setPage(1);
  }, [activeFilter, featuredFilter, premiumFilter, perPage]);

  const filterParams = useMemo(
    () => paramsForFilters({ activeFilter, featuredFilter, premiumFilter }),
    [activeFilter, featuredFilter, premiumFilter],
  );

  useEffect(() => {
    dispatch(fetchQuickTapsPage({
      page,
      per_page: perPage,
      sort: 'created_at',
      dir: 'desc',
      ...filterParams,
    }));
  }, [dispatch, page, perPage, filterParams]);

  const rows = paginated.data;
  const totalCount = paginated.total_count;
  const totalLabel = useMemo(() => totalCount.toLocaleString(), [totalCount]);

  return (
    <div>
      {/* Toolbar — `.toolbar` (prototype 838–842). Status segmented +
       *  Featured/Free/Premium dashed chips. All filters server-side. */}
      <div
        className="flex items-center flex-wrap"
        style={{ padding: '14px 28px 14px', gap: 8, borderBottom: '1px solid #E8E0D5' }}
      >
        <SegmentedControl
          value={activeFilter}
          onChange={setActiveFilter}
          options={[
            { value: 'all',      label: 'All',      count: activeFilter === 'all'      ? totalLabel : undefined },
            { value: 'active',   label: 'Active',   count: activeFilter === 'active'   ? totalLabel : undefined },
            { value: 'inactive', label: 'Inactive', count: activeFilter === 'inactive' ? totalLabel : undefined },
          ]}
        />
        <ChipFilter
          active={featuredFilter === 'yes'}
          onClick={() => setFeaturedFilter(featuredFilter === 'yes' ? 'all' : 'yes')}
          removable
        >Featured</ChipFilter>
        <ChipFilter
          active={premiumFilter === 'free'}
          onClick={() => setPremiumFilter(premiumFilter === 'free' ? 'all' : 'free')}
          removable
        >Free</ChipFilter>
        <ChipFilter
          active={premiumFilter === 'premium'}
          onClick={() => setPremiumFilter(premiumFilter === 'premium' ? 'all' : 'premium')}
          removable
        >Premium</ChipFilter>
      </div>

      {error && (
        <div className="px-7 py-3 text-coral-dark bg-coral-soft border-b border-coral/40 text-[13px]">
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-text-muted">
          Loading quick taps…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-text-muted py-16">
          No quick taps match the current filters.
        </div>
      ) : (
        <>
          <LibTable fixed>
            <QuickTapsColgroup />
            <QuickTapsHead />
            <tbody style={loading ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
              {rows.map((qt) => (
                <QuickTapRow key={qt.id} qt={qt} />
              ))}
            </tbody>
          </LibTable>
          <ListFooter
            page={paginated.page}
            perPage={paginated.per_page}
            totalCount={paginated.total_count}
            pageCount={paginated.page_count}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            itemLabel="quick taps"
          />
        </>
      )}
    </div>
  );
}
